import { useCallback, useEffect, useState } from "react"

import { Guid, IApiLink } from "@/types"
import { ErpInvoiceStatus, ReservationOutgoingInvoice } from "models/OutgoingInvoice"
import toastMessages from "stores/toastMessages"
import AssetService from "services/AssetService"

export default function useReservationInvoices(reservationId?: Guid, options?: { hydrateInvoicePositions?: boolean }) {
    const [invoices, setInvoices] = useState<ReservationOutgoingInvoice[] | null>(null)
    const [canCreateAdHocInvoice, setCanCreateAdHocInvoice] = useState<boolean>(false)
    const [linkCreateCollectiveCreditNote, setLinkCreateCollectiveCreditNote] = useState<IApiLink | null>(null)

    const fetchInvoices = useCallback(async () => {
        if (!reservationId) {
            setInvoices(null)
            setCanCreateAdHocInvoice(false)
            setLinkCreateCollectiveCreditNote(null)
            return null
        }
        try {
            const resp = await AssetService.getReservationInvoices(reservationId, {
                hydrateInvoicePositions: options?.hydrateInvoicePositions
            })
            setInvoices(resp.invoices)
            setCanCreateAdHocInvoice(resp.canCreateAdHocInvoice)
            setLinkCreateCollectiveCreditNote(resp.linkCreateCollectiveCreditNote || null)
            return resp.invoices
        } catch (e) {
            toastMessages.showSystemError()
            setInvoices([])
        }
    }, [options?.hydrateInvoicePositions, reservationId])

    useEffect(() => {
        fetchInvoices()
    }, [fetchInvoices])

    const refetchInvoices = useCallback(async () => {
        setCanCreateAdHocInvoice(false)
        setLinkCreateCollectiveCreditNote(null)
        return fetchInvoices()
    }, [fetchInvoices])

    function handleInvoiceResubmitted(invoiceId: Guid) {
        setInvoices(prev => {
            if (!prev) return prev;

            const idx = prev.findIndex(inv => inv.id === invoiceId);
            if (idx === -1) return prev;

            const target = prev[idx];

            const alreadyPending =
                target.linkResubmitInvoice === undefined &&
                target.erpOrderNumber === undefined &&
                target.erpOrderNumberMessage === undefined &&
                target.erpOrderNumberStatus === ErpInvoiceStatus.pending

            if (alreadyPending) return prev;

            const updated: ReservationOutgoingInvoice = {
                ...target,
                linkResubmitInvoice: undefined,
                erpOrderNumber: undefined,
                erpOrderNumberMessage: undefined,
                erpOrderNumberStatus: ErpInvoiceStatus.pending,
                rentalFeeAmount: target.rentalFeeAmount,
                residualAmount: target.residualAmount,
                printPeriod: target.printPeriod,
                getExchangeRateDate: target.getExchangeRateDate
            };

            const next = prev.slice();
            next[idx] = updated;
            return next;
        });
    }

    return {
        invoices,
        refetchInvoices,
        canCreateAdHocInvoice,
        linkCreateCollectiveCreditNote,
        handleInvoiceResubmitted,
    }
}
